import React from 'react';
import styles from './CampaignManagerInfluencerBar.module.css';
import { CachedAsset, CampaignManagerInfluencer, Currency, InfluencerCost } from '@round/api';
import { ReactComponent as TiktokIcon } from '../../../../assets/TiktokIcon.svg';
import { ReactComponent as InstagramIcon } from '../../../../assets/InstagramIcon.svg';
import { ReactComponent as YoutubeIcon } from '../../../../assets/YoutubeIcon.svg';
import { Button, Image } from '@round/ui-kit';
import cn from 'classnames';
import { asMoney, buildInstagramUserUrl, buildTiktokUserUrl, buildYoutubeUserUrl } from '../../../../utils/utits';

type CampaignManagerInfluencerBarProps = {
    influencer: CampaignManagerInfluencer;
    influencerCosts: InfluencerCost[];
    currency: Currency | undefined;
    image: CachedAsset | undefined;
    onClickEdit: (id: number) => void;
};

const CampaignManagerInfluencerBar = ({
    influencer,
    influencerCosts,
    currency,
    image,
    onClickEdit,
}: CampaignManagerInfluencerBarProps) => {
    const displayName = influencer.name || influencer.tiktok_display_name || influencer.instagram_display_name;
    const userName = influencer.tiktok_username ?? influencer.instagram_username;
    const userNameDisplayValue = userName ? `@${userName}` : '-';

    const tiktokUserLink = influencer.tiktok_username ? buildTiktokUserUrl(influencer.tiktok_username) : null;
    const instagramUserLink = influencer.instagram_username
        ? buildInstagramUserUrl(influencer.instagram_username)
        : null;
    const youtubeUserLink = influencer.youtube_username ? buildYoutubeUserUrl(influencer.youtube_username) : null;

    const tiktokAudioCost = influencerCosts.find((cost) => cost.platform === 'TIKTOK' && cost.campaign_type === 'AUDIO')
        ?.cost;
    const tiktokAudioCostDisplayValue = tiktokAudioCost ? asMoney(tiktokAudioCost, currency, 0) : '-';

    const tiktokBrandCost = influencerCosts.find((cost) => cost.platform === 'TIKTOK' && cost.campaign_type === 'BRAND')
        ?.cost;
    const tiktokBrandCostDisplayValue = tiktokBrandCost ? asMoney(tiktokBrandCost, currency, 0) : '-';

    const instagramAudioCost = influencerCosts.find(
        (cost) => cost.platform === 'INSTAGRAM' && cost.campaign_type === 'AUDIO'
    )?.cost;
    const instagramAudioCostDisplayValue = instagramAudioCost ? asMoney(instagramAudioCost, currency, 0) : '-';

    const instagramBrandCost = influencerCosts.find(
        (cost) => cost.platform === 'INSTAGRAM' && cost.campaign_type === 'BRAND'
    )?.cost;
    const instagramBrandCostDisplayValue = instagramBrandCost ? asMoney(instagramBrandCost, currency, 0) : '-';

    const youtubeAudioCost = influencerCosts.find(
        (cost) => cost.platform === 'YOUTUBE' && cost.campaign_type === 'AUDIO'
    )?.cost;
    const youtubeAudioCostDisplayValue = youtubeAudioCost ? asMoney(youtubeAudioCost, currency, 0) : '-';

    const youtubeBrandCost = influencerCosts.find(
        (cost) => cost.platform === 'YOUTUBE' && cost.campaign_type === 'BRAND'
    )?.cost;
    const youtubeBrandCostDisplayValue = youtubeBrandCost ? asMoney(youtubeBrandCost, currency, 0) : '-';

    return (
        <div className={cn(styles.barWrapper, { [styles.unavailable]: !influencer.is_available_for_booking })}>
            <div className={styles.influencerProfile}>
                <Image
                    className={styles.image}
                    src={image?.cached_url || image?.original_url || ''}
                    alt={`${displayName} image`}
                />
                <div className={styles.influencerInfo}>
                    <p className={styles.displayName}>{displayName}</p>
                    <p className={styles.userName}>{userNameDisplayValue}</p>
                </div>
            </div>
            <div className={styles.costStatsSection}>
                <div className={styles.costStat}>
                    <div
                        className={cn(styles.platformIconContainer, { [styles.linkLive]: tiktokUserLink })}
                        onClick={tiktokUserLink ? () => window.open(tiktokUserLink, '_blank') : undefined}
                    >
                        <TiktokIcon className={styles.platformIcon} />
                    </div>

                    <div className={styles.platformCosts}>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Audio</p>
                            <p className={styles.platformCostValue}>{tiktokAudioCostDisplayValue}</p>
                        </div>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Brand</p>
                            <p className={styles.platformCostValue}>{tiktokBrandCostDisplayValue}</p>
                        </div>
                    </div>
                </div>
                <div className={styles.costStat}>
                    <div
                        className={cn(styles.platformIconContainer, { [styles.linkLive]: instagramUserLink })}
                        onClick={instagramUserLink ? () => window.open(instagramUserLink, '_blank') : undefined}
                    >
                        <InstagramIcon className={styles.platformIcon} />
                    </div>

                    <div className={styles.platformCosts}>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Audio</p>
                            <p className={styles.platformCostValue}>{instagramAudioCostDisplayValue}</p>
                        </div>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Brand</p>
                            <p className={styles.platformCostValue}>{instagramBrandCostDisplayValue}</p>
                        </div>
                    </div>
                </div>
                <div className={styles.costStat}>
                    <div
                        className={cn(styles.platformIconContainer, { [styles.linkLive]: youtubeUserLink })}
                        onClick={youtubeUserLink ? () => window.open(youtubeUserLink, '_blank') : undefined}
                    >
                        <YoutubeIcon className={styles.platformIcon} />
                    </div>
                    <div className={styles.platformCosts}>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Audio</p>
                            <p className={styles.platformCostValue}>{youtubeAudioCostDisplayValue}</p>
                        </div>
                        <div className={styles.platformCostRow}>
                            <p className={styles.platformHeading}>Brand</p>
                            <p className={styles.platformCostValue}>{youtubeBrandCostDisplayValue}</p>
                        </div>
                    </div>
                </div>
            </div>
            <Button onClick={() => onClickEdit(influencer.id)} className={styles.editButton}>
                Edit creator details
            </Button>
        </div>
    );
};

export default CampaignManagerInfluencerBar;
